import { useState, useEffect, useRef, useCallback } from 'react'
import {
  ArrowSyncRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  DismissRegular,
  ChevronDownRegular,
  DocumentRegular,
  WarningRegular
} from '@fluentui/react-icons'
import { Spinner, SpinnerSize } from '@fluentui/react'

import { getCVUploadStatus, resolveCVDuplicate, submitCVMissingFields, dismissCVTask } from '../../api'
import { CVUploadTask } from '../../api/models'

import styles from './CVUploadPanel.module.css'

const POLL_INTERVAL = 2000

const ACTIVE_STATUSES = ['pending', 'extracting_text', 'ai_parsing', 'checking_duplicates', 'creating_candidate']

export const CVUploadPanel = () => {
  const [tasks, setTasks] = useState<CVUploadTask[]>([])
  const [expanded, setExpanded] = useState(false)
  const [visible, setVisible] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const hasActiveRef = useRef(false)
  const fetchIdRef = useRef(0)

  const fetchTasks = useCallback(async () => {
    // Track fetch sequence to ignore stale responses
    const id = ++fetchIdRef.current
    const result = await getCVUploadStatus()
    if (id !== fetchIdRef.current) return // stale response, discard
    let hasPlaceholders = false
    setTasks(prev => {
      const resultById = new Map(result.map(t => [t.task_id, t]))
      const resultFilenames = new Set(result.map(t => t.filename))

      // Keep placeholder tasks whose filename doesn't yet have a real backend task
      const remainingPlaceholders = prev.filter(
        t => t.task_id.startsWith('pending-') && !resultFilenames.has(t.filename)
      )
      hasPlaceholders = remainingPlaceholders.length > 0

      // Keep locally-held completed/failed tasks that the backend no longer returns
      const localTerminal = prev.filter(
        t => !t.task_id.startsWith('pending-') && (t.status === 'completed' || t.status === 'failed') && !resultById.has(t.task_id)
      )

      return [...remainingPlaceholders, ...result, ...localTerminal]
    })
    const hasActive = result.some(t => ACTIVE_STATUSES.includes(t.status) || t.status === 'duplicate_found' || t.status === 'missing_fields')
    hasActiveRef.current = hasActive
    // Show and auto-expand when new tasks arrive
    if (result.length > 0 && hasActive) {
      setVisible(true)
      setExpanded(true)
    }
    // Stop polling only when nothing is active AND no placeholders waiting for backend
    if (!hasActive && !hasPlaceholders && pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(() => {
      fetchTasks()
    }, POLL_INTERVAL)
  }, [fetchTasks])

  // Start/stop polling based on active tasks
  useEffect(() => {
    // Do an initial fetch
    fetchTasks()
    startPolling()

    // Listen for upload start — show optimistic placeholder tasks immediately,
    // then poll for real backend tasks.
    const handleUploadsStarted = (e: Event) => {
      const filenames: string[] = (e as CustomEvent).detail?.filenames || []
      if (filenames.length > 0) {
        // Create placeholder tasks so the panel shows all files instantly
        const placeholders: CVUploadTask[] = filenames.map((name, i) => ({
          task_id: `pending-${Date.now()}-${i}`,
          filename: name,
          status: 'pending' as const,
          progress_message: 'Uploading...',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          error: null,
          candidate_id: null,
          candidate_name: null,
          agencyid: 0,
          duplicate_info: null,
          extracted_data: null,
          missing_fields: null
        }))
        setTasks(prev => [...placeholders, ...prev])
      }
      setVisible(true)
      setExpanded(true)
      // Start polling — backend tasks will replace placeholders once created
      startPolling()
    }
    window.addEventListener('cv-uploads-started', handleUploadsStarted)

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
      }
      window.removeEventListener('cv-uploads-started', handleUploadsStarted)
    }
  }, [fetchTasks, startPolling])

  const handleResolve = async (taskId: string, action: 'update_existing' | 'force_create', existingCandidateId?: number) => {
    await resolveCVDuplicate(taskId, action, existingCandidateId)
  }

  const handleSubmitMissingFields = async (taskId: string, fieldValues: Record<string, string>) => {
    await submitCVMissingFields(taskId, fieldValues)
    // Restart polling to pick up status change
    startPolling()
  }

  // Trigger data refresh when a task completes
  useEffect(() => {
    const completedCount = tasks.filter(t => t.status === 'completed').length
    if (completedCount > 0) {
      window.dispatchEvent(new Event('app-data-changed'))
    }
  }, [tasks.filter(t => t.status === 'completed').length])

  const handleClose = async () => {
    // Dismiss all terminal tasks from the backend
    const terminalTasks = tasks.filter(t => !t.task_id.startsWith('pending-') && (t.status === 'completed' || t.status === 'failed'))
    await Promise.allSettled(terminalTasks.map(t => dismissCVTask(t.task_id)))
    setTasks([])
    setVisible(false)
    setExpanded(false)
  }

  if (!visible || tasks.length === 0) return null

  const activeCount = tasks.filter(t => ACTIVE_STATUSES.includes(t.status)).length
  const totalCount = tasks.length

  if (!expanded) {
    return (
      <div className={styles.panelContainer}>
        <div className={styles.collapsedWrapper}>
          <button className={styles.collapsedBadge} onClick={() => setExpanded(true)}>
            <DocumentRegular />
            Resume Uploads
            <span className={styles.badgeCount}>
              {activeCount > 0 ? `${activeCount} active` : totalCount}
            </span>
          </button>
          <button className={styles.closeBadgeButton} onClick={handleClose} title="Close">
            <DismissRegular />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.panelContainer}>
      <div className={styles.expandedPanel}>
        <div className={styles.panelHeader}>
          <span className={styles.panelTitle}>Resume Uploads ({totalCount})</span>
          <div className={styles.panelHeaderActions}>
            <button className={styles.collapseButton} onClick={() => setExpanded(false)} title="Collapse">
              <ChevronDownRegular />
            </button>
            <button className={styles.closeButton} onClick={handleClose} title="Close">
              <DismissRegular />
            </button>
          </div>
        </div>
        <div className={styles.taskList}>
          {tasks.map(task => (
            <TaskItem
              key={task.task_id}
              task={task}
              onResolve={handleResolve}
              onSubmitMissingFields={handleSubmitMissingFields}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

interface TaskItemProps {
  task: CVUploadTask
  onResolve: (taskId: string, action: 'update_existing' | 'force_create', existingCandidateId?: number) => void
  onSubmitMissingFields: (taskId: string, fieldValues: Record<string, string>) => void
}

const FIELD_LABELS: Record<string, string> = {
  first_name: 'First Name',
  last_name: 'Last Name',
  email: 'Email',
  phone: 'Phone',
}

const TaskItem = ({ task, onResolve, onSubmitMissingFields }: TaskItemProps) => {
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  const renderIcon = () => {
    switch (task.status) {
      case 'completed':
        return <CheckmarkCircleRegular style={{ color: '#107c10', fontSize: 18 }} />
      case 'failed':
        return <DismissCircleRegular style={{ color: '#d13438', fontSize: 18 }} />
      case 'duplicate_found':
      case 'missing_fields':
        return <WarningRegular style={{ color: '#ca5010', fontSize: 18 }} />
      default:
        return <Spinner size={SpinnerSize.xSmall} />
    }
  }

  const handleFieldChange = (field: string, value: string) => {
    setFieldValues(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmitFields = async () => {
    setSubmitting(true)
    await onSubmitMissingFields(task.task_id, fieldValues)
    setFieldValues({})
    setSubmitting(false)
  }

  const renderContent = () => {
    switch (task.status) {
      case 'completed':
        return (
          <span className={styles.taskSuccess}>
            {task.candidate_name} (ID: {task.candidate_id})
          </span>
        )
      case 'failed':
        return <span className={styles.taskError}>{task.error}</span>
      case 'missing_fields':
        return (
          <>
            <span className={styles.taskMessage}>{task.progress_message}</span>
            <div className={styles.missingFieldsForm}>
              {(task.missing_fields || []).map(field => (
                <div key={field} className={styles.fieldRow}>
                  <label className={styles.fieldLabel}>{FIELD_LABELS[field] || field}</label>
                  <input
                    className={styles.fieldInput}
                    type={field === 'email' ? 'email' : 'text'}
                    placeholder={FIELD_LABELS[field] || field}
                    value={fieldValues[field] || ''}
                    onChange={e => handleFieldChange(field, e.target.value)}
                    disabled={submitting}
                  />
                </div>
              ))}
              <button
                className={styles.duplicateButtonPrimary}
                onClick={handleSubmitFields}
                disabled={submitting || (task.missing_fields || []).some(f => !(fieldValues[f] || '').trim())}>
                {submitting ? 'Submitting...' : 'Continue'}
              </button>
            </div>
          </>
        )
      case 'duplicate_found':
        return (
          <>
            <span className={styles.taskMessage}>{task.progress_message}</span>
            {task.duplicate_info && (
              <div className={styles.duplicateInfo}>
                Existing: {task.duplicate_info.first_name} {task.duplicate_info.last_name} ({task.duplicate_info.match_type} match)
              </div>
            )}
            <div className={styles.duplicateActions}>
              <button
                className={styles.duplicateButtonPrimary}
                onClick={() => onResolve(task.task_id, 'update_existing', task.duplicate_info?.candidateid)}>
                Update Existing
              </button>
              <button
                className={styles.duplicateButton}
                onClick={() => onResolve(task.task_id, 'force_create')}>
                Create New
              </button>
            </div>
          </>
        )
      default:
        return <span className={styles.taskMessage}>{task.progress_message}</span>
    }
  }

  return (
    <div className={styles.taskItem}>
      <div className={styles.taskIcon}>{renderIcon()}</div>
      <div className={styles.taskContent}>
        <div className={styles.taskFilename} title={task.filename}>{task.filename}</div>
        {renderContent()}
      </div>
    </div>
  )
}

export default CVUploadPanel
